import * as qpService from '@api/quotes-policies';
import { PersonPageDetails } from '@api/person';
import * as pintService from '@api/pint';
import { ColoredButton, ColoredChip, ColoredChipIconButton } from '@components/colored-elements';
import { IngestionMethodChip } from '@components/ingestion-method';
import { RelationSourceChip } from '@components/relation-source-chip';
import { AiOutlinePlusCircle, BiEdit, MdDelete } from '@icon/react-icons';
import { dateToFormat, monthYearFormat, rbac, readableDate, useToggle, YmdStrToDate } from '@lib/common';
import { createReturnParam } from '@lib/deals';
import { Button, Dialog, DialogActions, DialogTitle, Tooltip, Typography } from '@material-ui/core';
import { blue, green, red } from '@material-ui/core/colors';
import CopyClipboard from '@material-ui/icons/AttachFile';
import { useSnackbar } from 'notistack';
import { CellForLastModifiedAt } from 'pages/company/search/columns/utils';
import ClipBoard, { AddAvailableFilter } from 'pages/person-management/person-master-search/columns/utils';
import React, { PropsWithChildren, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { CellProps, Column } from 'react-table';
import styled from 'styled-components';

import {
  $eq,
  BooleanFilters,
  DateFilters,
  DealFilter,
  StringFilters,
  withEnumMultiSelect,
  withMultiSelectAutoComplete,
} from '../filters';
import { $bw, $contains, } from '../filters/operators';
import {
  AddNonLinkedEntityFilter,
  addTitleToFilter,
//  BooleanFilters,
  BooleanFiltersWithBoolValues,
  CellForHQCountry,
  createEnumMap,
  CurrentlyWorkingBooleanFilters,
  SingleBuyerSellerCell,
  YesAndNoFilters,
} from './utils';
import { useRefreshCounter } from '../search/qp-search';
import { QPSourceType, QPStatementType, YesOrNoEnum } from '../quotes-policies.enums';
import { qpSearchDataType } from '../types';
import { companyMasterApi } from '@api/masters';
import { $in, withMultiSelectAutoCompleteTypes } from '@pages/company-content-beta/relation-search/filters';


type GlobalFilterColumn<D extends object = Record<string, any>> = Column<D> & {
  /**
   * Setting this to true will make the column come up in primary filters
   */
  primaryFilter?: boolean;
  /**
   * The operator to use with the primary filter. Use with `primaryFilter`
   */
  primaryFilterOperator?: string;
  /**
   * Force the columns to hide, this is usefull when you want to add a column
   * only to add the filter.
   */
  forceHide?: boolean;
  /**
   * A passive column is a column that can be used to filter, but
   * shows up in advanced filter.
   */
  passiveColumn?: boolean;
  overrideOperator?: { [k: string]: Partial<DealFilter> };
};

const TitledStringFilter = addTitleToFilter(StringFilters);
const EntityFilters = addTitleToFilter(AddNonLinkedEntityFilter(StringFilters));
const withAvailableFilters = AddAvailableFilter(StringFilters);

const FilterLabelsForParameters = ({ label, value }) => {
  return (
    <>{`${label} one of ${value
      .split(',')
      .map(element =>
        element
          .split(',')
          .map(x => x.split('|||')[1])
          .join(',')
      )
      .join(', ')}`}</>
  );
};
export const CellForCompEntity = () => (props: PropsWithChildren<CellProps<PersonPageDetails>>) => {
  const associated_comp: any = props?.row?.original;
  const RenderComponent = SingleBuyerSellerCell;

  if (!associated_comp?.company_name && !associated_comp?.company_vtr_id) {
    return null;
  }

  const firstEntityName = associated_comp?.company_name;
  return (
    <>
      <RenderComponent name={<>{firstEntityName} </>} vtrId={associated_comp?.company_vtr_id} />
    </>
  );
};

const YesOrNoChip = styled.div`
  padding: 10px;
  color: white;
  border-radius: 30px;
  width: fit-content;
`;

export const CurrentlyWorkingEntity =
  () => (props: PropsWithChildren<CellProps<PersonPageDetails>>) => {
    const original: any = props?.row?.original;

    const mapping = {
      [YesOrNoEnum.YES]: {
        icon: '✅',
        size: '20px',
        text: 'Yes',
      },
      [YesOrNoEnum.NO]: {
        icon: '❌',
        size: '18px',
        text: 'No',
      },
      [YesOrNoEnum.NOT_SURE]: {
        icon: '🤔',
        size: '20px',
        text: 'Not Sure',
      },
    };

    const currently_working_value = original?.currently_working;
    const icon = mapping[currently_working_value]?.icon || '';
    const size = mapping[currently_working_value]?.size;
    const text = mapping[currently_working_value]?.text || '';
    return (
      <Tooltip title={`Currently Working : ${text}`}>
        <YesOrNoChip style={{ fontSize: size }}>{icon}</YesOrNoChip>
      </Tooltip>
    );
  };

export const columns: GlobalFilterColumn<qpSearchDataType>[] = [
  {
    id: 'vtr_id',
    primaryFilter: true,
    primaryFilterOperator: $eq.key,
    Header: 'Vitrina Id',
    forceHide: true,
    disableFilters: true,
  },
  {
    accessor: 'statement_date',
    Header: 'Date',
    Cell: ({ value }) => readableDate(new Date(value)),
    overrideOperator: {
      [$eq.key]: {
        ChipLabel: ({ label, value }) => (
          <>
            {label} is {readableDate(YmdStrToDate(value))}
          </>
        ),
      },
      [$bw.key]: {
        ChipLabel: ({ label, value }) => (
          <>
            {readableDate(YmdStrToDate(value[0]))} ≤ {label} ≤{' '}
            {readableDate(YmdStrToDate(value[1]))}
          </>
        ),
      },
    },
    Filter: addTitleToFilter(
      DateFilters,
      'Quote/Policy Date',
      'Filter on the date this quote/policy was published.'
    ),
  },
  {
    id: 'company_name',
    accessor: 'company_name',
    primaryFilter: true,
    primaryFilterOperator: $contains.key,
    Header: 'Company',
    Filter: addTitleToFilter(withAvailableFilters, 'Company Name'),
    forceHide: true,
    //  disableFilters: true,
  },
  {
    id: 'statement_type',
    accessor: 'statement_type',
    Header: 'Type',
    Filter: addTitleToFilter(BooleanFilters, null, 'Filter type'),
    passiveColumn: true,
    forceHide: false,
    overrideOperator: {
      [$eq.key]: {
        ChipLabel: ({ label, value }) => (
          <>Type: {Object.values(QPStatementType).includes(value) ? value : '-'}</>
        ),
      },
    },
  },
  {
    Header: 'Company',
    Cell: CellForCompEntity(),
    minWidth: 200,
    disableSortBy: false,
  },
  {
     id: 'vtr_company_type_id',
     Header: 'Company Types',
     passiveColumn: true,
     forceHide: true,
     // Cell: CellForCompanyTypes,
     Filter: addTitleToFilter(
       withMultiSelectAutoCompleteTypes(
         companyMasterApi.getCompanyMasters,
         'types',
         'id',
         true,
         'type',
       ),
       // withMultiSelectAutoComplete(companyMasterApi.getCompanyMasters, 'type', 'id', true, true),
       null,
       'Filter Relations by Company Types',
     ),
     overrideOperator: {
       [$in.key]: {
         ChipLabel: FilterLabelsForParameters,
       },
     },
   },
  {
    id: 'person_name',
    accessor: row => row.person_name || 'NA',
    primaryFilter: true,
    primaryFilterOperator: $contains.key,
    Header: 'Name',
    minWidth: 120,
    disableFilters: false,
    Filter: addTitleToFilter(withAvailableFilters, 'Person Name'),
    Cell: ({ row }) => {
      const { person_vtr_id = '', person_name = '', person_role_id } = row?.original;
      return (
        <>
          <ClipBoard
            parentValue={person_name}
            icon={<CopyClipboard style={{ fontSize: '1.2em' }} color="primary" />}
            value={person_vtr_id ?? 'No Person VTR ID associated'}
            tooltipTitle={'Copy the Vitrina Id'}
          />
          <Typography variant="body2" color="textSecondary">
            {person_role_id}
          </Typography>
        </>
      );
    },
  },
  /*  {
    id: 'name_search',
    accessor: row => row.designation || 'NA',
    primaryFilter: false,
    primaryFilterOperator: $contains.key,
    forceHide: true,
    Header: 'Person',
    Filter: EntityFilters,
  }, */
  {
    id: 'designation',
    accessor: row => row.designation,
    primaryFilter: false,
    primaryFilterOperator: $contains.key,
    Header: 'Designation',
    Filter: EntityFilters,
    // minWidth: 150,
    // disableSortBy: true,
  },
  {
    id: 'statement',
    accessor: row => row.statement,
    primaryFilter: false,
    primaryFilterOperator: $contains.key,
    Header: 'Statement',
    disableFilters: true,
    Filter: EntityFilters,
    // minWidth: 150,
    // disableSortBy: true,
    Cell: ({ value }) => <StatementDiv title={value}>{value}</StatementDiv>,
  },
  /* {
    accessor: row => row.currently_working || '',
    Header: 'Currently Working',
    disableFilters: true,
    disableSortBy: true,
    Cell: CurrentlyWorkingEntity(),
  }, 
  {
    Header: 'Email Available',
    Cell: props => {
      const original: any = props?.row?.original;
      const email = original?.email || '';
      const hasEmail = email ? true : false;
      return (
        <Tooltip title={`Email Available : ${hasEmail ? 'Yes' : 'No'}`}>
          <span style={{ marginLeft: '10px' }}>
            {hasEmail ? (
              <span style={{ fontSize: '20px' }}>✅</span>
            ) : (
              <span style={{ fontSize: '18px' }}>❌</span>
            )}
          </span>
        </Tooltip>
      );
    },
    disableSortBy: false,
    primaryFilter: false,
  },*/
  /* {
    id: 'source',
    accessor: row => row.source || 'NA',
    primaryFilter: false,
    primaryFilterOperator: $eq.key,
    Header: 'Sources',
    // Filter: EntityFilters,
    // minWidth: 150,
    disableSortBy: true,
    disableFilters: true,
    Cell: ({ value }) => <RelationSourceChip type={value} />,
    Filter: addTitleToFilter(
      withEnumMultiSelect(createEnumMap(QPSourceType, true), true, true),
      null,
      'Filter Companies by Source'
    ),
    overrideOperator: {
      [$eq.key]: {
        ChipLabel: ({ label, value }) => (
          <>{`${label} one of ${value}
            `}</>
        ),
      },
    },
  }, */
  /*  {
    id: 'ingestion_method',
    accessor: row => row.ingestion_method || 'NA',
    primaryFilter: false,
    primaryFilterOperator: $eq.key,
    Header: 'Ingestion method',
    // Filter: EntityFilters,
    // minWidth: 150,
    disableSortBy: true,
    Cell: ({ value }) => <IngestionMethodChip type={value as unknown as C2PIngestionMethod} />,
    Filter: addTitleToFilter(
      withEnumMultiSelect(createEnumMap(C2PIngestionMethod, true), true, true),
      null,
      'Filter Companies by Ingestion Method'
    ),
    overrideOperator: {
      [$eq.key]: {
        ChipLabel: ({ label, value }) => (
          <>{`${label} one of ${value}
            `}</>
        ),
      },
    },
  }, */
  {
    id: 'modified_at',
    accessor: row => row?.modified_at || 'NA',
    primaryFilter: false,
    primaryFilterOperator: $contains.key,
    Header: 'Modified at',
    Cell: CellForLastModifiedAt,
    Filter: EntityFilters,
    // minWidth: 150,
    disableFilters: true,
  },
  {
    id: 'updated_by',
    accessor: row => row?.updated_by || row?.created_by ,
    Header: 'Modified By',
    Cell: ({ value }) => (
      <Tooltip title={value}>
        <ColoredChip colored={blue[500]} size="small" label={value} />
      </Tooltip>
    ),
    disableFilters: true,
    primaryFilter: false,
  },
  {
    id: 'actions',
    minWidth: 100,
    // maxWidth: 80,
    Header: 'Actions',
    Cell({ row }) {
      const returnTo = createReturnParam('/quotes-policies/search');
      const { vtr_id = '' } = row?.original;
      // const disableTheDeleteBtn =  currently_working === 'yes';
      return (
        <>
          <IconContainer>
            <Tooltip title="Edit QP">
              <ColoredChipIconButton
                component={Link}
                to={`/quotes-policies/edit/${vtr_id}${returnTo}`}
                pointer
                color="primary"
                size="medium"
                icon={<BiEdit />}
              />
            </Tooltip>
            <Tooltip title="Delete QP">
              <DeleteQPRelationDialog
                vtrId={vtr_id}
                // deleteDisable={disableTheDeleteBtn ? true : rbac('C2P_Delete', '', 'Button')}
                deleteQP
              />
            </Tooltip>
          </IconContainer>
        </>
      );
    },
  },
];

export const DeleteQPRelationDialog = React.forwardRef(
  (
    {
      buttonType = 'Icon',
      vtrId,
      //deleteDisable,
      deleteQP = false,
      ...props
    }: any,
    ref
  ) => {
    const { forceRefresh } = useRefreshCounter();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [deleteOpen, , showDeleteDialog, hideDeleteDialog] = useToggle(false);
    const [editingServiceMaster, setEditingServiceMaster] = useState(false);

    const onDeleteFinish = useCallback(RelationId => {
      const infoNotification = enqueueSnackbar('Please wait deleting Quotes & Policies', {
        variant: 'info',
      });
      return qpService.deleteQP({ vtr_id: RelationId }).subscribe({
        next: () => {
          enqueueSnackbar('Quotes & Policies deleted Successfully!', { variant: 'success' });
          // redirectToSearch();
          hideDeleteDialog();
          forceRefresh();
        },
        error: err => enqueueSnackbar(`Error deleting QP (${err})`, { variant: 'error' }),
        complete: () => closeSnackbar(infoNotification),
      });
    }, []);

    return (
      <div ref={ref as React.RefObject<HTMLDivElement>}>
        {buttonType === 'Icon' ? (
          deleteQP ? (
            <ColoredChipIconButton
              pointer
              onClick={showDeleteDialog}
              colored={red[700]}
              coloredHover={red[900]}
              size="medium"
              icon={<MdDelete />}
           //   disabled={deleteDisable}
              {...props} //Extra props are sent from tooltip to children
            />
          ) : (
            <ColoredChipIconButton
              pointer
              onClick={showDeleteDialog}
              // colored={blue[700]}
              // coloredHover={blue[900]}
              size="small"
              color="primary"
              icon={<BiEdit />}
              {...props} //Extra props are sent from tooltip to children
            />
          )
        ) : (
          <ColoredButton
            colored={green[700]}
            coloredHover={green[900]}
            startIcon={<AiOutlinePlusCircle />}
            component="button"
            onClick={showDeleteDialog}
            variant="contained">
            Add
          </ColoredButton>
        )}
        <QPDeleteDialog
          vtrId={vtrId}
          onDeleteFinish={onDeleteFinish}
          deleteQP
          open={deleteOpen}
          onClose={hideDeleteDialog}
        />
      </div>
    );
  }
);

export function QPDeleteDialog({
  vtrId,
  deleteQP = false,
  onDeleteFinish = null,
  onClose = null,
  open,
}) {
  return deleteQP ? (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description">
      <DialogTitle id="alert-dialog-title">
        Are you sure you want to delete this Quotes & Polices?
      </DialogTitle>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={() => onDeleteFinish(vtrId)} color="primary" autoFocus>
          Ok
        </Button>
      </DialogActions>
    </Dialog>
  ) : null;
}
const IconContainer = styled.span`
  display: flex;
  gap: 8px;
  padding: 15px 10px;
  justify-content: center;
`;

const StatementDiv = styled.div`
 display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4; /* change to 3 if needed */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  line-height: 1.4;
`;
